import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { AlertTriangle, GripVertical, X } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { Input } from '@orisini/ui/components/input'
import { cn } from '@orisini/ui/lib/utils'
import type { MediaDto } from '../api'

/** Slide yang sedang disusun di komposer, belum tentu tersimpan. */
export interface SlideDraft {
  mediaId: string
  altText: string
  media: MediaDto
}

export const MAX_SLIDES = 10

/**
 * Rasio yang diterima Instagram tanpa dipotong: 4:5 (0.8) sampai 1.91:1.
 * Di luar itu, Instagram memotongnya sendiri — dan agent akan MENOLAK
 * menerbitkannya daripada membiarkan foto terbit terpotong diam-diam.
 */
const RASIO_MIN = 0.8
const RASIO_MAKS = 1.91

function rasio(m: MediaDto): number | null {
  return m.width && m.height ? m.width / m.height : null
}

export function peringatanRasio(m: MediaDto): string | null {
  const r = rasio(m)
  if (r === null) return null
  if (r < RASIO_MIN) return `Too tall (${r.toFixed(2)}:1) — Instagram will crop it`
  if (r > RASIO_MAKS) return `Too wide (${r.toFixed(2)}:1) — Instagram will crop it`
  return null
}

function Baris({
  slide,
  index,
  label,
  onAlt,
  onHapus,
}: {
  slide: SlideDraft
  index: number
  label: string
  onAlt: (v: string) => void
  onHapus: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: slide.mediaId,
  })
  const peringatan = peringatanRasio(slide.media)

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-start gap-3 rounded-lg border bg-card p-3',
        isDragging && 'opacity-60 shadow-lg',
      )}
    >
      <button
        type="button"
        className="mt-1 cursor-grab text-muted-foreground hover:text-foreground"
        aria-label={`Move slide ${index + 1}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" />
      </button>

      <div className="size-16 shrink-0 overflow-hidden rounded bg-muted">
        {slide.media.previewUrl && slide.media.kind === 'image' ? (
          <img src={slide.media.previewUrl} alt="" className="size-full object-cover" />
        ) : (
          <div className="flex size-full items-center justify-center text-[10px] text-muted-foreground">
            {slide.media.kind === 'video' ? 'Video' : '—'}
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            {label}
            {slide.media.width && slide.media.height
              ? ` · ${slide.media.width}×${slide.media.height}`
              : ''}
          </span>
          <Button variant="ghost" size="sm" onClick={onHapus} aria-label={`Delete slide ${index + 1}`}>
            <X className="size-4" />
          </Button>
        </div>

        <Input
          value={slide.altText}
          onChange={(e) => onAlt(e.target.value)}
          placeholder="Alt text (optional)"
          className="h-8 text-sm"
        />

        {peringatan && (
          <p className="flex items-start gap-1.5 text-xs text-amber-500">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            {peringatan}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * Daftar slide yang bisa diurutkan.
 *
 * Urutan di sini ADALAH urutan yang dilihat audiens — server menyimpannya
 * menurut posisi array, bukan menurut `position` yang dikirim klien, supaya
 * tidak ada dua sumber kebenaran. Carousel yang terbit dengan urutan tertukar
 * tidak bisa diperbaiki tanpa menghapus postingannya.
 */
export default function SlideList({
  slides,
  onChange,
  format,
}: {
  slides: SlideDraft[]
  onChange: (next: SlideDraft[]) => void
  format: 'post' | 'reel'
}) {
  // Jarak aktivasi 6px: tanpa itu, klik biasa pada kolom alt text ikut
  // terbaca sebagai awal drag dan kolomnya jadi sulit difokuskan.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const dari = slides.findIndex((s) => s.mediaId === active.id)
    const ke = slides.findIndex((s) => s.mediaId === over.id)
    if (dari < 0 || ke < 0) return
    onChange(arrayMove(slides, dari, ke))
  }

  if (!slides.length) {
    return (
      <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
        No media yet. Add at least one through the panel below.
      </p>
    )
  }

  const bukanPersegiPanjangSeragam =
    format === 'post' &&
    slides.length > 1 &&
    new Set(
      slides.map((s) => {
        const r = rasio(s.media)
        return r === null ? 'x' : r.toFixed(2)
      }),
    ).size > 1

  return (
    <div className="space-y-3">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={slides.map((s) => s.mediaId)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {slides.map((s, i) => (
              <Baris
                key={s.mediaId}
                slide={s}
                index={i}
                label={format === 'reel' ? 'Reel video' : `Slide ${i + 1}`}
                onAlt={(v) =>
                  onChange(slides.map((x, j) => (j === i ? { ...x, altText: v } : x)))
                }
                onHapus={() => onChange(slides.filter((_, j) => j !== i))}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {bukanPersegiPanjangSeragam && (
        <p className="flex items-start gap-1.5 rounded-md bg-amber-500/10 p-2.5 text-xs text-amber-500">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          {/* Perilaku Instagram, bukan kami: seluruh carousel memakai rasio
              slide PERTAMA. Disebutkan di sini supaya tidak jadi kejutan
              setelah terbit — saat itu sudah tidak bisa diperbaiki. */}
          Slides have different aspect ratios. Instagram uses the first slide's ratio for the whole
          carousel, so the other slides will be cropped to match.
        </p>
      )}
    </div>
  )
}
